import React, { useState, useEffect } from "react";
import jsPDF from "jspdf";

// IDsystem now accepts locationData as a prop
function IDsystem({ locationData }) {
  const [idData, setIdData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchIdData = async () => {
      // Ensure all necessary location data is available before fetching
      if (!locationData || !locationData.provinceName || !locationData.municipalName || !locationData.barangayName) {
        setIdData([]); // Clear data if location is not fully selected
        setError("Please select a complete location (Province, Municipal, Barangay) from the dashboard to load ID data.");
        return;
      }

      setLoading(true);
      setError(null); // Clear previous errors

      const { provinceName, municipalName, barangayName } = locationData;
      // Construct the location string as required by the API
      const baseUrl = `${process.env.REACT_APP_API_URL}/api/locations/api/reports/id`
      const location = `${provinceName}_${municipalName}_${barangayName}`;
      const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}`;
      
      try {
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {'x-api-key': process.env.REACT_APP_API_KEY}
        });

        const data = await response.json();
        // Assuming the fetched data is an array of ID objects
        setIdData(data);
        if (data.length === 0) {
          setError("No ID data found for the selected location.");
        }
      } catch (err) {
        console.error("Error fetching ID data:", err);
        setError(`Failed to load ID data. Please ensure the backend server is running and the location is valid.`);
        setIdData([]); // Clear data on error
      } finally {
        setLoading(false);
      }
    };

    fetchIdData();
  }, [locationData]); // Re-run effect whenever locationData changes

  const downloadIDCardsPDF = () => { // Renamed function to reflect download action
    if (idData.length === 0) {
      alert("No ID data available to generate PDF. Please select a location and ensure data is loaded.");
      return;
    }

    // Initialize jsPDF in landscape mode (width, height)
    const doc = new jsPDF('landscape', 'pt', 'A4');

    // A4 Landscape dimensions in points
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Card layout parameters
    const numCols = 4;
    const numRows = 2;
    const cardsPerPage = numCols * numRows;

    const pageMargin = 30; // Margin from page edges
    const colSpacing = 10; // Space between columns of cards (reduced for tighter fit)
    const rowSpacing = 10; // Space between rows of cards (reduced for tighter fit)

    // Calculate usable area for cards
    const usableWidth = pageWidth - (2 * pageMargin);
    const usableHeight = pageHeight - (2 * pageMargin);

    // Calculate individual card dimensions
    const cardWidth = (usableWidth - (numCols - 1) * colSpacing) / numCols;
    const cardHeight = (usableHeight - (numRows - 1) * rowSpacing) / numRows;

    // Pre-calculate starting X positions for each column
    const colX = [];
    for (let i = 0; i < numCols; i++) {
      colX.push(pageMargin + i * (cardWidth + colSpacing));
    }

    // Pre-calculate starting Y positions for each row
    const rowY = [];
    for (let i = 0; i < numRows; i++) {
      rowY.push(pageMargin + i * (cardHeight + rowSpacing));
    }

    const textIndent = 5; // Margin from card left edge for text
    const labelDataSeparator = 60; // X-offset for data relative to label start (adjusted for new layout)
    const verticalTextSpacing = 15; // Vertical space between lines of text (adjusted for new layout)

    doc.setFontSize(8); // Default font size for labels and data

    idData.forEach((entry, index) => {
      const cardIndexOnPage = index % cardsPerPage;
      const colIndex = cardIndexOnPage % numCols;
      const rowIndex = Math.floor(cardIndexOnPage / numCols);

      const cardBaseX = colX[colIndex];
      const cardBaseY = rowY[rowIndex];

      // Add a new page if it's the first card on a new page (and not the very first card overall)
      if (cardIndexOnPage === 0 && index > 0) {
        doc.addPage();
      }

      // Draw card border
      doc.rect(cardBaseX, cardBaseY, cardWidth, cardHeight);

      // --- Card Content based on the provided image layout ---

      // Republic of the Philippines Header
      doc.setFontSize(7); // Smaller font for header
      doc.setFont(undefined, 'normal');
      doc.text('Republic of the Philippines', cardBaseX + cardWidth / 2, cardBaseY + 15, { align: 'center' });
      // Use locationData for Province, City/Municipality, and Barangay
      doc.text(`Province of ${locationData.provinceName || ''}`, cardBaseX + cardWidth / 2, cardBaseY + 25, { align: 'center' });
      doc.text(`City/Municipality of ${locationData.municipalName || ''}`, cardBaseX + cardWidth / 2, cardBaseY + 35, { align: 'center' });
      doc.text(`Barangay ${locationData.barangayName || ''}`, cardBaseX + cardWidth / 2, cardBaseY + 45, { align: 'center' });

      // Photo placeholder
      const photoBoxX = cardBaseX + 10;
      const photoBoxWidth = 72; // 1 inch = 72 points
      const photoBoxHeight = 72; // 1 inch = 72 points
      const photoBoxY = cardBaseY + 55; // Adjusted Y position if needed after size change

      doc.rect(photoBoxX, photoBoxY, photoBoxWidth, photoBoxHeight); // Photo box

      // ID No.
      doc.setFontSize(7); // Adjusted font size
      doc.setFont(undefined, 'normal');
      doc.text('ID No.', photoBoxX + photoBoxWidth + 2, photoBoxY + 10); // Adjusted X position
      doc.setFont(undefined, 'normal');
      doc.text(entry.id || '', photoBoxX + photoBoxWidth + 25, photoBoxY + 10); // Use entry.id for ID number
      doc.line(photoBoxX + photoBoxWidth + 25, photoBoxY + 12, cardBaseX + cardWidth - 10, photoBoxY + 12); // Underline for ID No.

      // Signature line
      doc.text('Signature', cardBaseX + cardWidth - 45, photoBoxY + photoBoxHeight - 25, { align: 'center' }); // Adjusted Y position
      doc.line(photoBoxX + photoBoxWidth + 25, photoBoxY + 32, cardBaseX + cardWidth - 10, photoBoxY + 32); // Underline for Signature


      let currentTextY = cardBaseY + 150; // Starting Y for personal info, adjusted for new layout

      // Helper function to add a field (label and data) for personal info
      const addField = (label, data) => {
        doc.setFontSize(7);
        if(label === 'Name :'){
            doc.setFont(undefined, 'bold');
        }
        else{
            doc.setFont(undefined, 'normal');
        }
        doc.text(label, cardBaseX + textIndent, currentTextY);
        doc.text(data || '', cardBaseX + textIndent + labelDataSeparator, currentTextY, {
          maxWidth: cardWidth - textIndent - labelDataSeparator - textIndent // Max width for data text
        });
        currentTextY += verticalTextSpacing;
      };

      // Name
      addField('Name :', entry.name);

      // Present Address
      addField('Present Address :', entry.address);

      // Place of Birth
      addField('Place of Birth:', entry.place_of_birth);

      // Birthdate
      // The fetched birthdate is an ISO string, convert it to a readable date string
      const birthdate = entry.birthdate ? new Date(entry.birthdate).toLocaleDateString('en-US') : '';
      addField('Birthdate:', birthdate);

      // Gender (using 'sex' from fetched data)
      addField('Gender:', entry.sex);

      // Civil Status
      addField('Civil Status:', entry.civil_status);

      // Occupation
      addField('Occupation:', entry.occupation);
    });

    // Construct filename from locationData
    const { provinceName, municipalName, barangayName } = locationData;
    const filename = `${provinceName || 'UnknownProvince'}_${municipalName || 'UnknownMunicipal'}_${barangayName || 'UnknownBarangay'}_IDs.pdf`;

    // Output the PDF for download
    doc.save(filename);
  };

  return (
    <div style={idSystemContainerStyle}>
      <h2 style={idSystemHeadingStyle}>Generate ID Cards</h2>
      {loading && <p style={messageStyle}>Loading ID data...</p>}
      {error && <p style={errorStyle}>{error}</p>}
      {!loading && idData.length === 0 && !error && (
        <p style={messageStyle}>No ID data found for the selected location. Please select a barangay and click "Apply Search" in the dashboard.</p>
      )}
      <button onClick={downloadIDCardsPDF} style={idSystemButtonStyle} disabled={loading || idData.length === 0}>
        Download ID Cards PDF {/* Updated button text */}
      </button>
    </div>
  );
}

// Styling to match ReportsDashboard's aesthetic
const idSystemContainerStyle = {
  width: "95%", // Adjusted to fill parent's width
  backgroundColor: "#ffffff",
  padding: "30px",
  borderRadius: "10px",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
  display: "flex",        // Added flex display
  flexDirection: "column", // Stack children vertically
  alignItems: "center",   // Center children horizontally
};

const idSystemHeadingStyle = {
  fontSize: "24px", // Matches heading in ReportsDashboard
  fontWeight: "bold",
  marginBottom: "20px",
  textAlign: "center",
  color: "#333",
};

const idSystemButtonStyle = {
  padding: "10px 20px",
  backgroundColor: "#007bff",
  color: "#fff",
  border: "none",
  borderRadius: "5px",
  fontWeight: "bold",
  cursor: "pointer",
  marginTop: "10px",
  transition: 'background-color 0.3s ease',
};

const messageStyle = {
  fontSize: '16px',
  color: '#555',
  marginTop: '10px',
  marginBottom: '10px',
  textAlign: 'center',
};

const errorStyle = {
  fontSize: '16px',
  color: '#dc3545',
  marginTop: '10px',
  marginBottom: '10px',
  textAlign: 'center',
  fontWeight: 'bold',
};

export default IDsystem;
